import { useQuery } from "@tanstack/react-query";
import { CloudServerOutlined, LoadingOutlined, ReloadOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useStore } from "zustand";

import { getActiveTyphoons, getImpact, getTyphoon, searchCities } from "./api/client";
import { AppShell } from "./components/AppShell";
import { CityInspector } from "./components/CityInspector";
import { HeaderBar } from "./components/HeaderBar";
import { HomePage } from "./components/HomePage";
import { Timeline } from "./components/Timeline";
import { TyphoonMap } from "./components/TyphoonMap";
import { TyphoonRail } from "./components/TyphoonRail";
import type { CityLocation, DataFreshnessState, WeatherImpactRow } from "./domain/types";
import { useCurrentTime, useOnlineStatus, useReducedMotion, useResponsiveLayout } from "./hooks/useAppEnvironment";
import { useUrlStateSync } from "./hooks/useUrlStateSync";
import { interpolateStormPoint, resolveLiveTimelineStart } from "./map/trackMath";
import { typhoonAppStore } from "./state/appStore";

const fiveMinutes = 5 * 60 * 1000;
const tiandituToken = (import.meta.env.VITE_TDT_TOKEN as string | undefined) ?? "";

function shouldPoll() {
  return typeof document === "undefined" || (document.visibilityState === "visible" && navigator.onLine);
}

export function App() {
  const route = window.location.pathname.replace(/\/+$/, "") || "/";
  if (route !== "/typhoon") return <HomePage />;

  return <TyphoonApp />;
}

function TyphoonApp() {
  const layout = useResponsiveLayout();
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const reducedMotion = useReducedMotion();
  const online = useOnlineStatus();
  const liveNow = useCurrentTime();
  const selectedCity = useStore(typhoonAppStore, (state) => state.selectedCity);
  const pinnedCities = useStore(typhoonAppStore, (state) => state.pinnedCities);
  const currentHour = useStore(typhoonAppStore, (state) => state.currentHour);
  const isPlaying = useStore(typhoonAppStore, (state) => state.isPlaying);
  const hiddenLayers = useStore(typhoonAppStore, (state) => state.hiddenLayers);
  const stormKey = useStore(typhoonAppStore, (state) => state.stormKey);
  const selectedWeatherRowId = useStore(typhoonAppStore, (state) => state.selectedWeatherRowId);
  const selectCity = useStore(typhoonAppStore, (state) => state.selectCity);
  const pinCity = useStore(typhoonAppStore, (state) => state.pinCity);
  const setCurrentHour = useStore(typhoonAppStore, (state) => state.setCurrentHour);
  const setPlaying = useStore(typhoonAppStore, (state) => state.setPlaying);
  const setStormKey = useStore(typhoonAppStore, (state) => state.setStormKey);
  const toggleLayer = useStore(typhoonAppStore, (state) => state.toggleLayer);
  const selectWeatherRow = useStore(typhoonAppStore, (state) => state.selectWeatherRow);
  const acknowledgeStormRefresh = useStore(typhoonAppStore, (state) => state.acknowledgeStormRefresh);

  useUrlStateSync({
    stormKey,
    selectedCityId: selectedCity?.id ?? null,
    pinnedCityIds: pinnedCities.map((city) => city.id),
    currentHour,
    hiddenLayers,
  });

  const activeQuery = useQuery({
    queryKey: ["active-typhoons"],
    queryFn: ({ signal }) => getActiveTyphoons(signal),
    refetchInterval: () => shouldPoll() ? fiveMinutes : false,
    refetchIntervalInBackground: false,
    staleTime: fiveMinutes,
  });
  const targetStorm = useMemo(() => {
    const active = activeQuery.data ?? [];
    return active.find((storm) => canonicalStormKey(storm) === stormKey)
      ?? active.find((storm) => storm.year === 2026 && storm.name.includes("巴威"))
      ?? active[0]
      ?? null;
  }, [activeQuery.data, stormKey]);
  useEffect(() => {
    if (targetStorm) setStormKey(canonicalStormKey(targetStorm));
  }, [setStormKey, targetStorm]);
  const trackQuery = useQuery({
    queryKey: ["typhoon", targetStorm?.source, targetStorm?.providerId],
    queryFn: ({ signal }) => getTyphoon(targetStorm!.source, targetStorm!.providerId, signal),
    enabled: Boolean(targetStorm),
    refetchInterval: () => shouldPoll() ? fiveMinutes : false,
    refetchIntervalInBackground: false,
    staleTime: fiveMinutes,
  });
  const impactQuery = useQuery({
    queryKey: ["impact", targetStorm?.providerId, selectedCity?.id],
    queryFn: ({ signal }) => getImpact(targetStorm!.providerId, selectedCity!.id, 72, "1h", signal),
    enabled: Boolean(targetStorm && selectedCity),
    refetchInterval: () => shouldPoll() ? fiveMinutes : false,
    refetchIntervalInBackground: false,
    staleTime: 30 * 60 * 1000,
  });

  const track = trackQuery.data;
  const rows = useMemo(() => impactQuery.data?.rows ?? [], [impactQuery.data?.rows]);
  const timelineStart = useMemo(() => {
    if (!track) return liveNow;
    return resolveLiveTimelineStart([...track.observed, ...track.forecast], liveNow);
  }, [liveNow, track]);
  const freshness: DataFreshnessState = !online
    ? "offline"
    : track?.freshness ?? (activeQuery.isError ? "offline" : "delayed");
  const currentPoint = useMemo(() => {
    if (!track) return null;
    const points = [...track.observed, ...track.forecast];
    return interpolateStormPoint(
      points,
      new Date(Date.parse(timelineStart) + currentHour * 3_600_000).toISOString(),
      track.windRadiiTimeline,
    );
  }, [currentHour, timelineStart, track]);

  const chooseCity = useCallback((city: CityLocation) => {
    selectCity(city);
    pinCity(city);
  }, [pinCity, selectCity]);
  const chooseWeatherRow = useCallback((row: WeatherImpactRow) => {
    selectWeatherRow(row.id);
    const origin = rows[0]?.startTime;
    if (origin) setCurrentHour((Date.parse(row.startTime) - Date.parse(origin)) / 3_600_000);
  }, [rows, selectWeatherRow, setCurrentHour]);
  const closeMobileNavigation = useCallback(() => setMobileNavigationOpen(false), []);
  const toggleMobileNavigation = useCallback(
    () => setMobileNavigationOpen((open) => !open),
    [],
  );

  useEffect(() => {
    if (track?.updatedAt) acknowledgeStormRefresh(track.updatedAt);
  }, [acknowledgeStormRefresh, track?.updatedAt]);

  if (!track) {
    const hasError = activeQuery.isError || trackQuery.isError || (activeQuery.isSuccess && !targetStorm);
    return (
      <AppShell
        header={<LoadingHeader />}
        inspector={<LoadingPanel label="城市 72 小时检查器" />}
        layout={layout}
        leftRail={<LoadingPanel label="台风信息与图层" />}
        map={<DataUnavailable error={hasError} onRetry={() => void activeQuery.refetch()} />}
        timeline={<LoadingPanel label="预测时间轴" />}
      />
    );
  }

  return (
    <AppShell
      header={
        <HeaderBar
          freshness={freshness}
          mobileNavigationOpen={mobileNavigationOpen}
          onCitySearch={searchCities}
          onCitySelect={chooseCity}
          onMobileNavigationToggle={toggleMobileNavigation}
          selectedCity={selectedCity}
          track={track}
        />
      }
      inspector={selectedCity ? (
        impactQuery.isError && rows.length === 0
          ? <PanelError message="城市天气与影响对比暂时不可用" onRetry={() => void impactQuery.refetch()} />
          : <CityInspector city={selectedCity} currentHour={currentHour} onRowSelect={chooseWeatherRow} rows={rows} selectedRowId={selectedWeatherRowId} />
      ) : <LoadingPanel label="请选择城市查看未来 72 小时" />}
      layout={layout}
      leftRail={<TyphoonRail currentPoint={currentPoint} hiddenLayers={hiddenLayers} onToggleLayer={toggleLayer} />}
      mobileNavigationOpen={mobileNavigationOpen}
      map={
        <TyphoonMap
          currentHour={currentHour}
          hiddenLayers={hiddenLayers}
          pinnedCities={pinnedCities}
          selectedCity={selectedCity}
          startTime={timelineStart}
          tiandituToken={tiandituToken}
          track={track}
        />
      }
      timeline={
        <Timeline
          currentHour={currentHour}
          isPlaying={isPlaying}
          onPlayingChange={setPlaying}
          onTimeChange={setCurrentHour}
          reducedMotion={reducedMotion}
          startTime={timelineStart}
        />
      }
      onMobileNavigationClose={closeMobileNavigation}
    />
  );
}

function LoadingHeader() {
  return <div className="topbar topbar--loading glass-panel"><h1>风迹</h1><span><LoadingOutlined />正在连接中国气象数据源</span></div>;
}

function LoadingPanel({ label }: { label: string }) {
  return <div className="panel-loading glass-panel"><LoadingOutlined /><span>{label}</span></div>;
}

function DataUnavailable({ error, onRetry }: { error: boolean; onRetry: () => void }) {
  if (!error) return <div className="map-loading"><LoadingOutlined /><span>正在加载台风路径与风圈</span></div>;
  return (
    <div className="data-unavailable" role="alert">
      <CloudServerOutlined aria-hidden />
      <strong>暂时无法取得台风数据</strong>
      <span>未启用前端模拟数据。请检查网络、服务端配置或稍后重试。</span>
      <button onClick={onRetry} type="button"><ReloadOutlined />重新获取</button>
    </div>
  );
}

function PanelError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return <div className="panel-error glass-panel" role="alert"><strong>{message}</strong><button onClick={onRetry} type="button">重试</button></div>;
}

function canonicalStormKey(storm: { source: string; basin: string; year: number; providerId: string }) {
  return `${storm.source}:${storm.basin}:${storm.year}:${storm.providerId}`;
}
